import { dapBffApiV2 } from '@dap/bff/data-access';
import {
  DeleteNotificationSubscriptionPayload,
  NotificationSettings,
  NotificationSubscription,
  UpdateNotificationSettingsPayload,
} from '@dap/notifications/types';

// These endpoints are injected into the shared `dapBffApiV2` slice (rather than a new
// `createApi` instance) so tag invalidation can cross-reference the 'NotificationStatus'
// tag that @dap/social-feed/data-access already provides/invalidates for the same
// underlying subscription documents.
const tags = ['NotificationSettings', 'NotificationSubscriptions'];

const notificationsApi = dapBffApiV2.enhanceEndpoints({ addTagTypes: tags }).injectEndpoints({
  endpoints: (build) => ({
    // Fetches the current user's global notification settings
    getNotificationSettings: build.query<NotificationSettings, void>({
      query: () => ({
        url: '/notifications/settings',
        method: 'GET',
      }),
      transformResponse: (response: { data: NotificationSettings }) => response.data,
      providesTags: ['NotificationSettings'],
    }),

    // Updates one or more of the current user's global notification settings
    updateNotificationSettings: build.mutation<
      NotificationSettings,
      UpdateNotificationSettingsPayload
    >({
      query: (payload) => ({
        url: '/notifications/settings',
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: { data: NotificationSettings }) => response.data,
      invalidatesTags: ['NotificationSettings'],
    }),

    // Fetches all of the current user's stored notification subscriptions, active or not
    getNotificationSubscriptions: build.query<NotificationSubscription[], void>({
      query: () => ({
        url: '/notifications/subscriptions',
        method: 'GET',
      }),
      transformResponse: (response: { data: NotificationSubscription[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map((subscription) => ({
                type: 'NotificationSubscriptions' as const,
                id: subscription.targetId,
              })),
              'NotificationSubscriptions' as const,
            ]
          : ['NotificationSubscriptions' as const],
    }),

    // Deletes a stored notification subscription entirely (as opposed to toggling it off,
    // which is handled by useToggleNotificationMutation from @dap/social-feed/data-access)
    deleteNotificationSubscription: build.mutation<void, DeleteNotificationSubscriptionPayload>({
      query: (payload) => ({
        url: `/notifications/subscriptions/${payload.targetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'NotificationSubscriptions', id: arg.targetId },
        { type: 'NotificationStatus', id: arg.targetId },
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useGetNotificationSubscriptionsQuery,
  useDeleteNotificationSubscriptionMutation,
} = notificationsApi;
